import React from "react"

import { createStandaloneToast } from "@chakra-ui/react"

import { AdminPermission } from "@prisma/client"

import { intersects } from "@supliful/domain"

import { defaultChakraToastOptions } from "../config"

export const getLogoByCarrierSlug = (carrier?: string): string | undefined => {
  let logo
  const logoMap = {
    fedex: "/logos/fedex-logo.png",
    usps: "/logos/usps-logo.png",
    ups: "/logos/ups-logo.png",
    passport: "/logos/passport-logo.png",
    dhl: "/logos/dhl-logo.png",
    pasts: "/logos/latvijas-pasts-logo.png",
  }

  if (!carrier) {
    return undefined
  }

  Object.entries(logoMap).forEach(([slug, src]) => {
    if (carrier.toLowerCase().includes(slug)) {
      logo = src
    }
  })

  return logo
}

export const getCarrierExternalIdBySlug = ({
  carrierSlug,
  warehouseId,
  carriers = [],
}: {
  carrierSlug: string
  warehouseId: string
  carriers: { warehouseId: string; slug: string; externalId: string }[]
}): string | undefined => {
  return carriers.find(
    (carrier) =>
      carrier.warehouseId === warehouseId && carrier.slug === carrierSlug,
  )?.externalId
}

const { toast } = createStandaloneToast({
  defaultOptions: defaultChakraToastOptions,
})

export const printShippingLabel = async ({
  labelUrl,
  onLoadingStart,
  onLoadingEnd,
}: {
  labelUrl: string | string[]
  onLoadingStart?: () => void
  onLoadingEnd?: () => void
}) => {
  // Since print-js depends on the `window` object, load it dynamically on the client side.
  // @see https://nextjs.org/docs/advanced-features/dynamic-import#with-external-libraries
  const printJS = (await import("print-js")).default

  const firstLabelUrl = Array.isArray(labelUrl) ? labelUrl[0] : labelUrl
  const extension = firstLabelUrl.split(".").pop()?.toLowerCase()
  const type = extension === "pdf" ? "pdf" : "image"

  printJS({
    printable: labelUrl,
    type,
    showModal: true,
    onLoadingStart: () => {
      if (onLoadingStart) onLoadingStart()
    },
    onLoadingEnd: () => {
      if (onLoadingEnd) onLoadingEnd()
    },
    // Without this, print-js rethrows into the void and the user only sees
    // the loading modal blink (e.g. when the label host is blocked on their
    // network) — the failure must be visible and carry its own recovery,
    // since not every caller renders an open-in-new-tab control.
    onError: (error: unknown) => {
      const detail =
        error instanceof Error ? error.message : String(error ?? "")
      toast({
        status: "error",
        title: "Failed to load the label for printing",
        description: React.createElement(
          "span",
          null,
          detail ? `${detail}. ` : "",
          React.createElement(
            "a",
            {
              href: firstLabelUrl,
              target: "_blank",
              rel: "noopener noreferrer",
              style: { textDecoration: "underline" },
            },
            Array.isArray(labelUrl) && labelUrl.length > 1
              ? "Open the first label in a new tab"
              : "Open the label in a new tab",
          ),
          " to view and print it.",
        ),
      })
    },
  })
}

export const isAuthorized = (
  userPermissions: AdminPermission[] | undefined,
  permissionsNeeded: AdminPermission[],
) => {
  // user must either have EVERYTHING permission or one of the permissions listed in permissionsNeeded
  return intersects(userPermissions, [
    AdminPermission.EVERYTHING,
    ...permissionsNeeded,
  ])
}
